import type SessionService from '@efdigital/ll-addon-auth/services/session';
import type { CoachProfile } from '@efdigital/ll-addon-data';
import { QuickStartLink } from '@efdigital/ll-xceed-v2/components/coaching/quick-start-link.gts';
import type CoachService from '@efdigital/ll-xceed-v2/services/coach';
import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';

export interface OnboardingStep {
  description: string;
  blurb: string;
  action: string;
  actionRoute: string;
  number: number;
  status: 'complete' | 'current' | 'upcoming';
  title: string;
}

function eq(a: unknown, b: unknown): boolean {
  return a === b;
}

/**
 * The checklist is sequential, so only the current step (the next thing to do)
 * links to its action — completed and upcoming steps are not clickable.
 */
function isActionable(step: OnboardingStep): boolean {
  return step.status === 'current' && Boolean(step.actionRoute);
}

function getStepStatus(
  previousStep: boolean,
  currentStep: boolean
): 'complete' | 'current' | 'upcoming' {
  if (!previousStep) {
    return 'upcoming';
  }
  if (!currentStep) {
    return 'current';
  }
  return 'complete';
}

/**
 * The icon + text for one checklist row — shared by the clickable (LinkTo) and
 * plain variants so the markup isn't duplicated.
 */
const StepRow: TOC<{ Args: { step: OnboardingStep } }> = <template>
  <span
    class="ll-avatar-xs rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0
      {{if (eq @step.status 'complete') 'text-bg-success'}}
      {{if (eq @step.status 'current') 'bg-body text-body border'}}
      {{if (eq @step.status 'upcoming') 'border border-2 text-body-secondary'}}"
    aria-hidden="true"
  >
    {{#if (eq @step.status "complete")}}
      <iconify-icon inline icon="ph:check-bold" class="text-white" />
    {{else}}
      <span class="small fw-bold">{{@step.number}}</span>
    {{/if}}
  </span>

  <span class="min-w-0">
    <span class="visually-hidden">
      {{#if (eq @step.status "complete")}}Completed.{{else if
        (eq @step.status "current")
      }}Current step.{{else}}Upcoming.{{/if}}
    </span>
    <span
      class="d-block fw-semibold
        {{if (eq @step.status 'upcoming') 'text-body-secondary'}}"
    >{{@step.title}}</span>
    <span class="d-block small text-body-secondary">{{@step.description}}</span>
  </span>
</template>;

export class OnboardingChecklist extends Component<{
  Args: { coach: CoachProfile };
  Blocks: {
    default: [currentStep?: OnboardingStep];
  };
  Element: HTMLDivElement;
}> {
  @service declare session: SessionService;
  @service declare coach: CoachService;

  get step1() {
    return true;
  }

  get step2() {
    return this.args.coach.profileConfirmed;
  }

  get step3() {
    return this.step2 && this.coach.status.hasCronofyAccount;
  }

  get step4() {
    // The coach only reaches this dashboard once their profile is published, so
    // "your profile goes live once review is complete" is already satisfied.
    // Returning that lets the checklist dismiss once steps 2 and 3 are done,
    // instead of leaving step 4 perpetually "current".
    return this.args.coach.status === 'published';
  }

  get steps(): OnboardingStep[] {
    return [
      {
        number: 1,
        title: 'Create your account',
        description: 'You signed in and joined the coach network.',
        blurb: '',
        action: '',
        actionRoute: '',
        status: getStepStatus(true, this.step1),
      },
      {
        number: 2,
        title: 'Complete your profile',
        description:
          'Add your photo, bio and specialties, then confirm your profile to complete this step.',
        blurb:
          'Add your photo, bio, specialties, and coaching credentials, then confirm your profile. It will be reviewed before you can be matched with coachees.',
        action: 'Set up profile',
        actionRoute: 'coaching.coach.settings.index',
        status: getStepStatus(this.step1, this.step2),
      },
      {
        number: 3,
        title: 'Set your availability',
        description:
          'Choose when you are open for chemistry calls and sessions.',
        blurb:
          'Connect your calendar and set your availability so coachees can book sessions with you.',
        action: 'Connect your calendar',
        actionRoute: 'coaching.coach.settings.availability',
        status: getStepStatus(this.step2, this.step3),
      },
      {
        number: 4,
        title: 'Get matched with coachees',
        description: 'Your profile goes live once review is complete.',
        blurb: '',
        action: '',
        actionRoute: '',
        status: getStepStatus(this.step3, this.step4),
      },
    ];
  }

  get currentStep() {
    return this.steps.find((step) => step.status === 'current');
  }

  <template>
    {{#unless this.coach.status.loading}}
      {{#let this.currentStep as |currentStep|}}
        {{#if currentStep}}
          <div ...attributes>
            <QuickStartLink
              @route="coaching.coach.help"
              @icon="ph:lightbulb"
              @title="Coach quick start guide"
              @description="How coaching works here, working with your coachees, and the tools for sessions, notes, and invoicing."
              @wide={{true}}
              class="mb-3"
            />
            <div class="row g-3">
              <div class="col-lg-6">
                <div class="card border-0 text-bg-primary h-100 rounded-4">
                  <div
                    class="card-body d-flex flex-column justify-content-center align-items-start text-start h-100 p-3 p-md-4"
                  >
                    <p class="gud-eyebrow">
                      Your focus right now
                    </p>

                    <h2 class="h4 fw-bold mb-2">{{currentStep.title}}</h2>

                    <p class="mb-4 pe-lg-5">
                      {{currentStep.description}}
                    </p>

                    {{#if currentStep.actionRoute}}
                      <LinkTo
                        @route={{currentStep.actionRoute}}
                        class="btn btn-dark rounded-pill"
                      >
                        {{currentStep.action}}
                        <iconify-icon
                          inline
                          icon="ph:arrow-right"
                          class="ms-1"
                          aria-hidden="true"
                        />
                      </LinkTo>
                    {{/if}}
                  </div>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="card border shadow-sm rounded-4 h-100">
                  <div class="card-body p-3 p-md-4">
                    <h2
                      id="onboarding-checklist-heading"
                      class="h5 fw-bold mb-4"
                    >Your onboarding checklist</h2>

                    <ol
                      class="list-unstyled d-flex flex-column mb-0"
                      aria-labelledby="onboarding-checklist-heading"
                    >
                      {{#each this.steps as |step|}}
                        <li>
                          {{#if (isActionable step)}}
                            <LinkTo
                              @route={{step.actionRoute}}
                              class="ll-checklist-step d-flex align-items-center gap-3 text-reset text-decoration-none rounded-3 p-2"
                            >
                              <StepRow @step={{step}} />
                              <iconify-icon
                                inline
                                icon="ph:caret-right"
                                class="ms-auto flex-shrink-0 text-body-secondary"
                                aria-hidden="true"
                              />
                            </LinkTo>
                          {{else}}
                            <span class="d-flex align-items-center gap-3 p-2">
                              <StepRow @step={{step}} />
                            </span>
                          {{/if}}
                        </li>
                      {{/each}}
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </div>
        {{/if}}
        {{yield currentStep}}
      {{/let}}
    {{/unless}}
  </template>
}
